import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";

const EMPTY_FORM = {
  name: "",
  starting_price: "",
  description: "",
  tier: "standard",
  age_note: "",
  bullets: [""],
};

export default function VenuesPage() {
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = () => {
    setLoading(true);
    api.get("/venues")
      .then(({ data }) => setVenues(data.venues || []))
      .catch(() => toast.error("Failed to load packages"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const std = venues.filter((v) => (v.tier || "standard") === "standard");
    const pre = venues.filter((v) => v.tier === "premium");
    const other = venues.filter((v) => v.tier && v.tier !== "standard" && v.tier !== "premium");
    return { std, pre, other };
  }, [venues]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowEditor(true);
  };

  const openEdit = (v) => {
    setEditing(v.id);
    setForm({
      name: v.name || "",
      starting_price: v.starting_price ? String(v.starting_price) : "",
      description: v.description || "",
      tier: v.tier || "standard",
      age_note: v.age_note || "",
      bullets: v.bullets && v.bullets.length ? [...v.bullets] : [""],
    });
    setShowEditor(true);
  };

  const setBullet = (i, value) => {
    const next = [...form.bullets];
    next[i] = value;
    setForm({ ...form, bullets: next });
  };
  const addBullet = () => setForm({ ...form, bullets: [...form.bullets, ""] });
  const removeBullet = (i) => {
    const next = [...form.bullets];
    next.splice(i, 1);
    setForm({ ...form, bullets: next.length ? next : [""] });
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Package name required"); return; }
    const payload = {
      name: form.name.trim(),
      starting_price: parseFloat(form.starting_price) || 0,
      description: form.description.trim(),
      tier: form.tier || "standard",
      age_note: (form.age_note || "").trim(),
      bullets: form.bullets.map((b) => b.trim()).filter(Boolean),
    };
    try {
      if (editing) {
        await api.put(`/venues/${editing}`, payload);
        toast.success("Package updated");
      } else {
        await api.post("/venues", payload);
        toast.success("Package added");
      }
      setShowEditor(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const handleDelete = async (v) => {
    if (!window.confirm(`Delete package "${v.name}"? This can't be undone.`)) return;
    try {
      await api.delete(`/venues/${v.id}`);
      toast.success("Deleted");
      setVenues((cur) => cur.filter((x) => x.id !== v.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const Section = ({ title, subtitle, items, tier }) => (
    <section className="space-y-3">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-lg font-semibold">{title}</h2>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </div>
        <Badge variant="outline" className="text-xs">{items.length} {items.length === 1 ? "package" : "packages"}</Badge>
      </div>
      {items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          No {tier} packages yet
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((v) => (
            <Card key={v.id} className="border-border" data-testid={`venue-${v.id}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold truncate">{v.name}</h3>
                    {v.age_note && (
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">{v.age_note}</p>
                    )}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(v)} data-testid={`venue-edit-${v.id}`}><Pencil size={12} /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-600" onClick={() => handleDelete(v)} data-testid={`venue-delete-${v.id}`}><Trash2 size={12} /></Button>
                  </div>
                </div>
                <p className="text-xl font-mono font-bold text-primary">
                  {v.starting_price ? `$${v.starting_price}` : <span className="text-sm font-sans text-muted-foreground italic">No price set</span>}
                </p>
                {v.bullets && v.bullets.length > 0 ? (
                  <ul className="space-y-1">
                    {v.bullets.map((b, i) => (
                      <li key={i} className="text-sm text-muted-foreground flex items-start gap-1.5">
                        <span className="text-primary mt-0.5">•</span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  v.description && <p className="text-sm text-muted-foreground">{v.description}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </section>
  );

  return (
    <div className="space-y-6" data-testid="venues-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit text-2xl font-bold tracking-tight">Birthday Packages</h1>
          <p className="text-sm text-muted-foreground">Standard and Premium party packages shown on the public inquiry form.</p>
        </div>
        <Button size="sm" onClick={openNew} data-testid="add-venue-btn">
          <Plus size={16} /> Add Package
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-28 w-full" />)}</div>
      ) : (
        <div className="space-y-8">
          <Section title="Party Packages" subtitle="Up to 15 guests · 25 for enrolled campers" items={grouped.std} tier="standard" />
          <Section title="Premium Packages" subtitle="Up to 20 guests · 25 for enrolled campers" items={grouped.pre} tier="premium" />
          {grouped.other.length > 0 && <Section title="Other" subtitle="" items={grouped.other} tier="other" />}
        </div>
      )}

      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Edit Package" : "Add Package"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5 col-span-2">
                <label className="text-xs font-medium text-muted-foreground">Name</label>
                <Input data-testid="venue-name" placeholder="e.g. The Artist" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Tier</label>
                <Select value={form.tier} onValueChange={(v) => setForm({ ...form, tier: v })}>
                  <SelectTrigger data-testid="venue-tier"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Party Package</SelectItem>
                    <SelectItem value="premium">Premium Package</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Starting Price ($)</label>
                <Input data-testid="venue-price" type="number" placeholder="0" value={form.starting_price} onChange={(e) => setForm({ ...form, starting_price: e.target.value })} />
              </div>
              <div className="space-y-1.5 col-span-2">
                <label className="text-xs font-medium text-muted-foreground">Age Note (optional)</label>
                <Input data-testid="venue-age-note" placeholder="e.g. Ages 7 and under" value={form.age_note} onChange={(e) => setForm({ ...form, age_note: e.target.value })} />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground">What's included (bullet points)</label>
              {form.bullets.map((b, i) => (
                <div key={i} className="flex gap-2">
                  <Input
                    data-testid={`venue-bullet-${i}`}
                    placeholder={i === 0 ? "e.g. Interactive Art Project" : "Another included activity"}
                    value={b}
                    onChange={(e) => setBullet(i, e.target.value)}
                  />
                  {form.bullets.length > 1 && (
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeBullet(i)}><X size={14} /></Button>
                  )}
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={addBullet}>
                <Plus size={14} /> Add bullet
              </Button>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowEditor(false)}>Cancel</Button>
            <Button data-testid="venue-save" onClick={handleSave}>{editing ? "Update Package" : "Create Package"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
